<!-- Main Content with Loading Overlay -->
<div class="revenue-container" [class.loading]="loading">
  <!-- Loading Overlay -->
  @if (loading) {
    <div class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="loading-text mt-3">Loading revenue data...</p>
      </div>
    </div>
  }

  <!-- Header Section -->
  <div class="revenue-header">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="page-title">Revenue Analytics</h2>
        <p class="page-subtitle">Track your earnings and transaction history</p>
      </div>

      <!-- Refresh Button -->
      <button
        class="btn btn-refresh"
        [disabled]="refreshing || loading"
        (click)="refreshData()">
        <i class="fa fa-sync-alt" [class.fa-spin]="refreshing"></i>
        @if (refreshing) {
          <span class="ms-2">Refreshing...</span>
        } @else {
          <span class="ms-2">Refresh</span>
        }
      </button>
    </div>

    <!-- Filter Section -->
    <div class="filter-section">
      <div class="filter-header mb-3">
        <h5 class="filter-title">
          <i class="fa fa-filter me-2"></i>
          Filter by Date Range
        </h5>
      </div>

      <!-- Quick Filters -->
      <div class="quick-filters mb-3">
        @for (filter of quickFilters; track filter.key) {
          <button
            class="btn btn-quick-filter"
            [class.active]="filterType === filter.key"
            [disabled]="revenueLoading || transactionsLoading"
            (click)="setDateFilter(filter.key)">
            <i class="fa" [class]="filter.icon"></i>
            <span>{{ filter.label }}</span>
          </button>
        }
      </div>

      <!-- Custom Date Range -->
      @if (showDatePicker) {
        <div class="custom-date-range">
          <div class="date-range-inputs">
            <div class="date-input-group">
              <label>Start Date</label>
              <input
                type="date"
                class="form-control"
                [(ngModel)]="startDate"
                [max]="endDate || today">
            </div>
            <div class="date-separator">
              <i class="fa fa-arrow-right"></i>
            </div>
            <div class="date-input-group">
              <label>End Date</label>
              <input
                type="date"
                class="form-control"
                [(ngModel)]="endDate"
                [min]="startDate"
                [max]="today">
            </div>
            <button
              class="btn btn-apply-date"
              [disabled]="!startDate || !endDate"
              (click)="applyCustomDateFilter(startDate, endDate)">
              <i class="fa fa-check"></i>
              Apply
            </button>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Revenue Cards Section -->
  <div class="revenue-cards">
    <div class="row g-4">
      <!-- Total Revenue Card -->
      <div class="col-lg-3 col-md-6">
        <div class="revenue-card total-card">
          <div class="card-overlay" [class.visible]="revenueLoading"></div>
          @if (revenueLoading) {
            <div class="card-loading">
              <div class="loading-skeleton"></div>
            </div>
          } @else {
            <div class="card-content">
              <div class="card-icon">
                <i class="fa fa-chart-line"></i>
              </div>
              <div class="card-info">
                <h3 class="card-title">Total Revenue</h3>
                <p class="card-amount">{{ (waiterRevenue?.total || 0) | currency: '₦' }}</p>
                <span class="card-trend positive">
                  <i class="fa fa-arrow-up"></i> +12.5%
                </span>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Cash Transactions Card -->
      <div class="col-lg-3 col-md-6">
        <div class="revenue-card cash-card">
          <div class="card-overlay" [class.visible]="revenueLoading"></div>
          @if (revenueLoading) {
            <div class="card-loading">
              <div class="loading-skeleton"></div>
            </div>
          } @else {
            <div class="card-content">
              <div class="card-icon">
                <i class="fa fa-money-bill-wave"></i>
              </div>
              <div class="card-info">
                <h3 class="card-title">Cash Payments</h3>
                <p class="card-amount">{{ (waiterRevenue?.cash_transactions || 0) | currency: '₦' }}</p>
                <span class="card-percentage">
                  {{ ((waiterRevenue?.cash_transactions / waiterRevenue?.total) * 100 | number: '1.0-1') || 0 }}% of total
                </span>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Transfer Transactions Card -->
      <div class="col-lg-3 col-md-6">
        <div class="revenue-card transfer-card">
          <div class="card-overlay" [class.visible]="revenueLoading"></div>
          @if (revenueLoading) {
            <div class="card-loading">
              <div class="loading-skeleton"></div>
            </div>
          } @else {
            <div class="card-content">
              <div class="card-icon">
                <i class="fa fa-exchange-alt"></i>
              </div>
              <div class="card-info">
                <h3 class="card-title">Bank Transfers</h3>
                <p class="card-amount">{{ (waiterRevenue?.transfer_transactions || 0) | currency: '₦' }}</p>
                <span class="card-percentage">
                  {{ ((waiterRevenue?.transfer_transactions / waiterRevenue?.total) * 100 | number: '1.0-1') || 0 }}% of total
                </span>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- POS Transactions Card -->
      <div class="col-lg-3 col-md-6">
        <div class="revenue-card pos-card">
          <div class="card-overlay" [class.visible]="revenueLoading"></div>
          @if (revenueLoading) {
            <div class="card-loading">
              <div class="loading-skeleton"></div>
            </div>
          } @else {
            <div class="card-content">
              <div class="card-icon">
                <i class="fa fa-credit-card"></i>
              </div>
              <div class="card-info">
                <h3 class="card-title">Card (POS)</h3>
                <p class="card-amount">{{ (waiterRevenue?.pos_transactions || 0) | currency: '₦' }}</p>
                <span class="card-percentage">
                  {{ ((waiterRevenue?.pos_transactions / waiterRevenue?.total) * 100 | number: '1.0-1') || 0 }}% of total
                </span>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- Transaction History Section -->
  <div class="transaction-history-section">
    <div class="section-header">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 class="section-title">Transaction History</h3>
          <p class="section-subtitle">Detailed breakdown of all transactions</p>
        </div>

        <!-- Search Bar -->
        <div class="search-container">
          <div class="search-input-group">
            <i class="fa fa-search search-icon"></i>
            <input
              type="text"
              class="form-control search-input"
              placeholder="Search transactions..."
              [(ngModel)]="searchTerm"
              (input)="onSearch($event)">
            @if (searchTerm) {
              <button class="btn-clear-search" (click)="searchTerm = ''; onSearch($event)">
                <i class="fa fa-times"></i>
              </button>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div class="transactions-table-container">
      <div class="table-overlay" [class.visible]="transactionsLoading"></div>

      @if (transactionsLoading) {
        <div class="table-loading">
          <div class="loading-spinner">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading transactions...</span>
            </div>
            <p class="loading-text mt-3">Loading transaction history...</p>
          </div>
        </div>
      } @else if (filteredTransactions && filteredTransactions.length > 0) {
        <div class="table-responsive">
          <table class="table transactions-table">
            <thead>
              <tr>
                <th>
                  <i class="fa fa-hashtag me-2"></i>
                  Order ID
                </th>
                <th>
                  <i class="fa fa-money-bill me-2"></i>
                  Amount
                </th>
                <th>
                  <i class="fa fa-credit-card me-2"></i>
                  Payment Method
                </th>
                <th>
                  <i class="fa fa-check-circle me-2"></i>
                  Status
                </th>
                <th>
                  <i class="fa fa-calendar me-2"></i>
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              @for (item of filteredTransactions | paginate: {
                itemsPerPage: itemsPerPage || 10,
                currentPage: page,
                totalItems: total
              }; track item.order_id) {
                <tr class="transaction-row">
                  <td class="order-id-cell">
                    <span class="order-id">#{{ item.order_id }}</span>
                  </td>
                  <td class="amount-cell">
                    <span class="amount">{{ item.amount | currency: '₦' }}</span>
                  </td>
                  <td class="payment-method-cell">
                    <div class="payment-method">
                      <div class="payment-icon" [class]="'payment-' + item.payment_method?.toLowerCase()">
                        @switch (item.payment_method?.toLowerCase()) {
                          @case ('cash') { <i class="fa fa-money-bill"></i> }
                          @case ('transfer') { <i class="fa fa-exchange-alt"></i> }
                          @case ('pos') { <i class="fa fa-credit-card"></i> }
                          @default { <i class="fa fa-payment"></i> }
                        }
                      </div>
                      <span>{{ capitalizeFirstLetter(item.payment_method) }}</span>
                    </div>
                  </td>
                  <td class="status-cell">
                    <span class="status-badge" [class]="'status-' + item.status?.toLowerCase()">
                      <i class="fa fa-circle status-dot"></i>
                      {{ capitalizeFirstLetter(item.status) }}
                    </span>
                  </td>
                  <td class="date-cell">
                    <div class="date-info">
                      <span class="date">{{ item.created_at | date: 'MMM dd, yyyy' }}</span>
                      <span class="time">{{ item.created_at | date: 'hh:mm a' }}</span>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        @if (total > (itemsPerPage || 10)) {
          <div class="pagination-container">
            <pagination-controls
              class="modern-pagination"
              (pageChange)="onPageChange($event)"
              [responsive]="true">
            </pagination-controls>
          </div>
        }
      } @else {
        <div class="empty-state">
          <div class="empty-icon">
            <i class="fa fa-receipt"></i>
          </div>
          <h4 class="empty-title">No transactions found</h4>
          <p class="empty-message">
            @if (searchTerm) {
              No transactions match your search criteria.
            } @else {
              No transactions available for the selected date range.
            }
          </p>
          @if (searchTerm) {
            <button class="btn btn-primary" (click)="searchTerm = ''; onSearch($event)">
              Clear Search
            </button>
          }
        </div>
      }
    </div>
  </div>
</div>
